import React, { useEffect, useMemo, useRef, useState } from 'react'
import swal from 'sweetalert'
import html2pdf from 'html2pdf.js'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBriefcase,
  faCalendarAlt,
  faClock,
  faDownload,
  faFileAlt,
  faMoneyBillWave,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons'
import { db } from '../firebase'
import logo from '../Images/logo-main-black.png'
import signature from '../Images/signature.png'

const normalizeTimestamp = (value) => {
  if (!value) return 0
  if (typeof value?.toMillis === 'function') return value.toMillis()
  if (typeof value?.seconds === 'number') return value.seconds * 1000
  if (typeof value === 'number') return value

  const parsedValue = new Date(value).getTime()
  return Number.isFinite(parsedValue) ? parsedValue : 0
}

const formatDate = (value, fallback = 'Not set') => {
  const timestamp = normalizeTimestamp(value)
  if (!timestamp) return fallback

  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
  }).format(new Date(timestamp))
}

const formatDateTime = (value, fallback = 'Not set') => {
  const timestamp = normalizeTimestamp(value)
  if (!timestamp) return fallback

  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp))
}

const normalizeText = (value) => String(value || '').trim()

const sanitizeFileName = (value) => {
  const safeValue = normalizeText(value)
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '')

  return safeValue || 'student'
}

const resolveStudentTraineeOnboardedAt = (traineeRecord, userProfile) => {
  return (
    userProfile?.studentTraineeOnboardedAt ||
    traineeRecord?.onboardedAt ||
    traineeRecord?.createdAt ||
    userProfile?.studentTraineeUpdatedAt ||
    null
  )
}

const resolveStudentTraineeCompensation = (traineeRecord, userProfile) => {
  return (
    userProfile?.traineeCompensation ||
    userProfile?.traineeCompensationExpectation ||
    userProfile?.traineeCompensationAmount ||
    traineeRecord?.compensation ||
    traineeRecord?.compensationExpectation ||
    traineeRecord?.compensationAmount ||
    'Not set'
  )
}

function StudentTraineeComp() {
  const [isLoading, setIsLoading] = useState(true)
  const [isDownloadingLetter, setIsDownloadingLetter] = useState(false)
  const [userProfile, setUserProfile] = useState(null)
  const [studentTraineeRecord, setStudentTraineeRecord] = useState(null)

  const auth = getAuth()
  const letterRef = useRef(null)

  useEffect(() => {
    let userProfileUnsubscribe = null
    let traineeUnsubscribe = null

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setUserProfile(null)
        setStudentTraineeRecord(null)
        setIsLoading(false)
        return
      }

      setIsLoading(true)

      let hasUserProfile = false
      let hasTraineeProfile = false

      const finishInitialLoad = () => {
        if (hasUserProfile && hasTraineeProfile) {
          setIsLoading(false)
        }
      }

      userProfileUnsubscribe = onSnapshot(
        query(collection(db, 'users'), where('userId', '==', user.uid)),
        (snapshot) => {
          hasUserProfile = true
          setUserProfile(snapshot.docs[0]?.data() || null)
          finishInitialLoad()
        },
        (error) => {
          console.error('Error loading student trainee user profile:', error)
          hasUserProfile = true
          setIsLoading(false)
          swal('Unable to load page', 'We could not load your trainee information right now. Please refresh and try again.', 'error')
        }
      )

      traineeUnsubscribe = onSnapshot(
        doc(db, 'studentTrainees', user.uid),
        (snapshot) => {
          hasTraineeProfile = true
          setStudentTraineeRecord(snapshot.exists() ? snapshot.data() : null)
          finishInitialLoad()
        },
        (error) => {
          console.error('Error loading student trainee record:', error)
          hasTraineeProfile = true
          setIsLoading(false)
          swal('Unable to load page', 'We could not load your trainee information right now. Please refresh and try again.', 'error')
        }
      )
    })

    return () => {
      unsubscribe()
      if (userProfileUnsubscribe) userProfileUnsubscribe()
      if (traineeUnsubscribe) traineeUnsubscribe()
    }
  }, [auth])

  const normalizedUserStatus = normalizeText(userProfile?.userStatus || userProfile?.Status).toLowerCase()
  const canAccessProgram = normalizedUserStatus === 'completed'

  const traineeSummary = useMemo(() => {
    if (!studentTraineeRecord) return null

    return {
      fullName: studentTraineeRecord.fullName || userProfile?.bio || 'Student',
      course: studentTraineeRecord.course || userProfile?.course || 'Not set',
      cohort: userProfile?.cohort || studentTraineeRecord.cohort || 'Not set',
      traineeArea: studentTraineeRecord.traineeArea || studentTraineeRecord.traineeTrack || 'Not set',
      availability: studentTraineeRecord.availability || 'Not set',
      hoursPerDay: studentTraineeRecord.hoursPerDay || studentTraineeRecord.hoursPerWeek || 'Not set',
      preferredTerm: studentTraineeRecord.preferredTerm || studentTraineeRecord.termPreference || 'Not set',
      compensation: resolveStudentTraineeCompensation(studentTraineeRecord, userProfile),
      traineeStatus: studentTraineeRecord.traineeStatus || studentTraineeRecord.applicationStatus || 'Onboarded',
      onboardedAt: resolveStudentTraineeOnboardedAt(studentTraineeRecord, userProfile),
      updatedAt: studentTraineeRecord.updatedAt || null,
      admissionNumber: studentTraineeRecord.admissionNumber || userProfile?.admissionNumber || userProfile?.studentId || 'Not set',
    }
  }, [studentTraineeRecord, userProfile])

  const downloadPlacementLetter = async () => {
    if (!letterRef.current || !traineeSummary) return

    setIsDownloadingLetter(true)

    try {
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready
      }

      const images = letterRef.current.querySelectorAll('img')
      await Promise.all(
        Array.from(images).map((image) => (
          image.complete ? Promise.resolve() : new Promise((resolve) => {
            image.onload = resolve
            image.onerror = resolve
          })
        ))
      )

      await html2pdf()
        .from(letterRef.current)
        .set({
          margin: 10,
          filename: `Student_Trainee_Placement_Letter_${sanitizeFileName(traineeSummary.fullName)}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .save()
    } catch (error) {
      console.error('Error exporting placement letter:', error)
      swal('Download failed', 'We could not prepare your placement letter right now. Please try again.', 'error')
    } finally {
      setIsDownloadingLetter(false)
    }
  }

  if (isLoading) {
    return (
      <div className='dashMain traineePage traineePageMinimal'>
        <div className='traineeLoadingCard traineeLoadingCardMinimal'>
          <h2>Loading trainee page...</h2>
          <p>We are preparing your trainee details.</p>
        </div>
      </div>
    )
  }

  if (!canAccessProgram) {
    return (
      <div className='dashMain traineePage traineePageMinimal'>
        <div className='traineeMinimalShell'>
          <section className='traineeMinimalHero'>
            <p className='traineeMinimalEyebrow'>Career Growth</p>
            <h1>Student Trainee</h1>
            <p>This program is available to students who have graduated.</p>
          </section>
        </div>
      </div>
    )
  }

  return (
    <div className='dashMain traineePage traineePageMinimal'>
      <div className='traineeMinimalShell'>
        <section className='traineeMinimalHero'>
          <div>
            <p className='traineeMinimalEyebrow'>Career Growth</p>
            <h1>Student Trainee</h1>
            <p>
              Review your trainee placement details and download your student trainee placement letter.
            </p>
          </div>
          <span className={`traineeMinimalStatus ${traineeSummary ? 'is-active' : ''}`}>
            {traineeSummary ? 'Onboarded' : 'Awaiting onboarding'}
          </span>
        </section>

        {traineeSummary ? (
          <>
            <section className='traineeMinimalGrid'>
              <article className='traineeMinimalCard traineeMinimalCardMain'>
                <div className='traineeMinimalCardHeader'>
                  <div>
                    <p className='traineeMinimalSectionLabel'>Placement Summary</p>
                    <h2>{traineeSummary.traineeArea}</h2>
                  </div>
                  <div className='traineeMinimalMeta'>
                    <span>Updated {formatDateTime(traineeSummary.updatedAt)}</span>
                  </div>
                </div>

                <div className='traineeMinimalInfoGrid'>
                  <div className='traineeMinimalInfoItem'>
                    <span>Student</span>
                    <strong>{traineeSummary.fullName}</strong>
                  </div>
                  <div className='traineeMinimalInfoItem'>
                    <span>Programme</span>
                    <strong>{traineeSummary.course}</strong>
                  </div>
                  <div className='traineeMinimalInfoItem'>
                    <span>Cohort</span>
                    <strong>{traineeSummary.cohort}</strong>
                  </div>
                  <div className='traineeMinimalInfoItem'>
                    <span>Admission number</span>
                    <strong>{traineeSummary.admissionNumber}</strong>
                  </div>
                  <div className='traineeMinimalInfoItem'>
                    <span>Availability</span>
                    <strong>{traineeSummary.availability}</strong>
                  </div>
                  <div className='traineeMinimalInfoItem'>
                    <span>Hours per day</span>
                    <strong>{traineeSummary.hoursPerDay}</strong>
                  </div>
                  <div className='traineeMinimalInfoItem'>
                    <span>Preferred term</span>
                    <strong>{traineeSummary.preferredTerm}</strong>
                  </div>
                  <div className='traineeMinimalInfoItem'>
                    <span>Compensation</span>
                    <strong>{traineeSummary.compensation}</strong>
                  </div>
                </div>
              </article>

              <aside className='traineeMinimalSidebar'>
                <article className='traineeMinimalCard'>
                  <p className='traineeMinimalSectionLabel'>Overview</p>
                  <div className='traineeMinimalList'>
                    <div>
                      <span><FontAwesomeIcon icon={faUserCheck} /> Status</span>
                      <strong>{traineeSummary.traineeStatus}</strong>
                    </div>
                    <div>
                      <span><FontAwesomeIcon icon={faBriefcase} /> Area</span>
                      <strong>{traineeSummary.traineeArea}</strong>
                    </div>
                    <div>
                      <span><FontAwesomeIcon icon={faCalendarAlt} /> Onboarded</span>
                      <strong>{formatDate(traineeSummary.onboardedAt)}</strong>
                    </div>
                    <div>
                      <span><FontAwesomeIcon icon={faClock} /> Term</span>
                      <strong>{traineeSummary.preferredTerm}</strong>
                    </div>
                    <div>
                      <span><FontAwesomeIcon icon={faMoneyBillWave} /> Compensation</span>
                      <strong>{traineeSummary.compensation}</strong>
                    </div>
                  </div>
                </article>
              </aside>
            </section>

            <section className='traineeMinimalDocuments'>
              <article className='traineeMinimalCard'>
                <div className='traineeMinimalCardHeader'>
                  <div>
                    <p className='traineeMinimalSectionLabel'>Placement Letter</p>
                    <h2>Student Trainee Placement Letter</h2>
                  </div>
                </div>

                <p className='traineeMinimalBodyCopy'>
                  Download a PDF copy of your student trainee placement letter containing your area, term, availability,
                  daily hours, compensation, and the official college signature.
                </p>

                <div className='traineeMinimalLetterBar'>
                  <div className='traineeMinimalLetterMeta'>
                    <span><FontAwesomeIcon icon={faFileAlt} /> Letter type</span>
                    <strong>Student Trainee Placement Letter</strong>
                  </div>
                  <button
                    type='button'
                    className='traineeMinimalDownloadBtn'
                    onClick={downloadPlacementLetter}
                    disabled={isDownloadingLetter}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    {isDownloadingLetter ? 'Preparing PDF...' : 'Download PDF'}
                  </button>
                </div>

                <p className='traineeMinimalLegalNote'>
                  This letter confirms onboarding into the student trainee program. It does not serve as an employment contract.
                </p>
              </article>
            </section>
          </>
        ) : (
          <section className='traineeMinimalDocuments'>
            <article className='traineeMinimalCard'>
              <p className='traineeMinimalSectionLabel'>Awaiting Admin Onboarding</p>
              <h2>No trainee details yet</h2>
              <p className='traineeMinimalBodyCopy'>
                Your admin has not onboarded you as a student trainee yet. Once they do, your area, availability,
                daily hours, compensation, and placement letter will appear here.
              </p>
            </article>
          </section>
        )}
      </div>

      {traineeSummary && (
        <div className='traineeLetterStage' aria-hidden='true'>
          <div ref={letterRef} className='traineeLetterDocument'>
            <div className='traineeLetterHeader'>
              <img src={logo} alt='Ubunifu College' className='traineeLetterLogo' />
              <div className='traineeLetterHeaderCopy'>
                <p>UBUNIFU COLLEGE</p>
                <span>Developing the capacity to innovate</span>
              </div>
            </div>

            <div className='traineeLetterBody'>
              <div className='traineeLetterTopRow'>
                <span>Date: {formatDate(traineeSummary.onboardedAt || traineeSummary.updatedAt)}</span>
                <span>Ref: Student Trainee Program</span>
              </div>

              <h1>Student Trainee Placement Letter</h1>

              <p>Dear {traineeSummary.fullName},</p>

              <p>
                This letter confirms your onboarding into the Ubunifu College Student Trainee Program following your completion
                of studies. The details of your current placement are outlined below.
              </p>

              <p>
                Your trainee placement is under the <strong>{traineeSummary.traineeArea}</strong> area. You completed the
                <strong> {traineeSummary.course}</strong> programme under the <strong>{traineeSummary.cohort}</strong> cohort,
                with admission number <strong>{traineeSummary.admissionNumber}</strong>.
              </p>

              <p>
                For the <strong>{traineeSummary.preferredTerm}</strong> term, your recorded availability is
                <strong> {traineeSummary.availability}</strong>, with an expected commitment of
                <strong> {traineeSummary.hoursPerDay}</strong> per day. The recorded compensation for this trainee placement is
                <strong> {traineeSummary.compensation}</strong> per month. This is the expected monthly amount, though it may
                be paid weekly, every two weeks, or monthly depending on the financial capacity of the school or based on how
                assigned clients or students make their payments. This compensation may also be reviewed and increased based
                on good performance during the trainee placement period.
              </p>

              <p>
                Please keep this letter for your records. It confirms your placement within the student trainee program and
                clarifies the current trainee terms recorded by the college.
              </p>

              <p className='traineeLetterDisclaimer'>
                This placement letter is issued for the student trainee program and does not constitute an employment contract.
              </p>

              <div className='traineeLetterSignatureBlock'>
                <img src={signature} alt='Signature' className='traineeLetterSignature' />
                <div className='traineeLetterSignatureLine' />
                <strong>REINHARD OMUKUBA</strong>
                <span>DIRECTOR</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StudentTraineeComp
